import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react'
import {
  Button,
  fontSize,
  Modal,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  NewSelect,
  spacing,
  Text,
} from '@retailer-platform/shared-components'
import { startCase } from 'lodash'
import { AddIcon, CloseIcon, SearchIcon, GearIcon } from '@instacart/ids-core'
import { SwitchBase } from '@instacart/ids-tooling'
import { css } from '@emotion/react'
import { Input } from '../md-platform-list/components/MdPlatformModifyModal.tsx'
import {
  AuditWorkflowColumnConfigType,
  type QualityAuditWorkflowColumnConfig,
  type QualityAuditWorkflowColumnConfigInput,
} from '../../types/qualityAuditWorkflows.types.ts'
import {
  Divider,
  Property,
  PropertySection,
  PropertyTitle,
  Row,
  Spacer,
} from '../../utils/common.styles.tsx'
import {
  cssPropertyListElementButton,
  PropertyListContainer,
  PropertyListElement,
} from '../../pages/quality-audit-workflow-modify/CatalogAdminQualityAuditWorkflowModifyPage.tsx'
import { useDomainMessages } from '../../utils/domain/intl.ts'
import { PropertyContainer } from '../shared-styled-components/SharedStyledComponents.tsx'
import { QualityAuditWorkflowConfigColumnInputModal } from './components/QualityAuditWorkflowConfigColumnInputModal.tsx'
import { QualityAuditWorkflowConfigColumnFeatureModal } from './components/QualityAuditWorkflowConfigColumnFeatureModal.tsx'

const cssPropertyContainer = css({
  gap: `${spacing.X16}`,
  margin: 0,
  width: '100%',
  alignItems: 'baseline',
})

interface Props {
  columnConfig: QualityAuditWorkflowColumnConfig
  onClose: () => void
  onSave: (columnConfig: QualityAuditWorkflowColumnConfig) => void
}

export const QualityAuditWorkflowConfigColumnModal: FunctionComponent<
  React.PropsWithChildren<Props>
> = ({ onClose, onSave, columnConfig }) => {
  const [inputConfigModalState, setInputConfigModalState] = useState<
    { index: number; input: QualityAuditWorkflowColumnConfigInput } | undefined
  >(undefined)
  const [columnConfigModalState, setColumnConfigModalState] = useState<
    QualityAuditWorkflowColumnConfig | undefined
  >(undefined)
  const [newColumnConfig, setNewColumnConfig] =
    useState<QualityAuditWorkflowColumnConfig>(columnConfig)

  const i18n = useDomainMessages({
    title: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.modal.title',
    createTitle:
      'catalogAdminDomain.qualityAuditWorkflows.configs.columns.input.modal.title.create',
    aliasLabel: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.modal.alias.label',
    aliasDetails: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.modal.alias.details',
    renderLabel: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.modal.render.label',
    renderDetails: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.modal.render.details',
    inputsLabel: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.modal.inputs.label',
    inputsDetails: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.modal.inputs.details',
    inputsFlaggable:
      'catalogAdminDomain.qualityAuditWorkflows.configs.columns.modal.inputs.flaggable',
    inputsSuggestible:
      'catalogAdminDomain.qualityAuditWorkflows.configs.columns.modal.inputs.suggestible',
    inputSearchable:
      'catalogAdminDomain.qualityAuditWorkflows.configs.columns.modal.inputs.searchable',
    subType: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.modal.inputs.subType',
    save: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.save',
    close: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.close',
  })

  const optionTypes = Object.values(AuditWorkflowColumnConfigType)
    .map(value => ({
      label: startCase(value),
      value: value.valueOf(),
    }))
    .sort((a, b) => a.value.localeCompare(b.value))
  const subOptionTypes = optionTypes.filter(
    optionType =>
      optionType.value !== AuditWorkflowColumnConfigType.multi_type &&
      optionType.value !== AuditWorkflowColumnConfigType.image &&
      optionType.value !== AuditWorkflowColumnConfigType.labeled_image
  )

  const inputs = useMemo(() => newColumnConfig?.inputs ?? [], [newColumnConfig?.inputs])

  const onAliasChange = (value: string) => {
    setNewColumnConfig({
      ...newColumnConfig,
      alias: value,
    })
  }

  const onTypeChange = (value: string) => {
    if (value === AuditWorkflowColumnConfigType.entity_id) {
      setNewColumnConfig({
        ...newColumnConfig,
        type: value,
        inputs: [],
      })
    } else if (value === AuditWorkflowColumnConfigType.multi_type) {
      setNewColumnConfig({
        ...newColumnConfig,
        inputs: newColumnConfig.inputs.map(input => ({
          ...input,
          subType: AuditWorkflowColumnConfigType.string,
          subAlias: startCase(input.name),
          flaggable: false,
          suggestible: false,
        })),
        type: value,
      })
    } else {
      setNewColumnConfig({
        ...newColumnConfig,
        inputs: newColumnConfig.inputs.map(input => {
          delete input.subType
          delete input.subAlias
          return {
            ...input,
          }
        }),
        type: value,
      })
    }
  }

  const onInputChange = useCallback(
    (input: QualityAuditWorkflowColumnConfigInput, index: number) => {
      const newInputs = [...(newColumnConfig?.inputs ?? [])]
      newInputs[index] = input
      setNewColumnConfig({
        ...newColumnConfig,
        inputs: newInputs,
      })
    },
    [newColumnConfig]
  )

  const showInputModal = (input: QualityAuditWorkflowColumnConfigInput, index: number) => {
    setInputConfigModalState({
      index,
      input: input ?? {},
    })
  }

  const onColumnIndexChange = (newIndex: number, oldIndex: number) => {
    if (newIndex < 0 || newIndex >= inputs.length) return

    const tempInput = inputs[newIndex]
    inputs[newIndex] = inputs[oldIndex]
    inputs[oldIndex] = tempInput

    setNewColumnConfig({
      ...newColumnConfig,
      inputs: [...inputs],
    })
  }
  const onColumnIndexDelete = (targetIndex: number) => {
    if (targetIndex < 0 || targetIndex >= inputs.length) return

    setNewColumnConfig({
      ...newColumnConfig,
      inputs: [...inputs.filter((_, index) => index !== targetIndex)],
    })
  }

  const onInputSave = (input: QualityAuditWorkflowColumnConfigInput, index: number) => {
    if (index < 0) return
    const newInputsState = [...inputs]
    if (index < inputs.length) {
      newInputsState[index] = input
    } else {
      newInputsState.push(input)
    }
    setNewColumnConfig({
      ...newColumnConfig,
      inputs: newInputsState,
    })
    setInputConfigModalState(undefined)
  }

  const showInputs =
    newColumnConfig?.type !== AuditWorkflowColumnConfigType.entity_id &&
    newColumnConfig?.type !== AuditWorkflowColumnConfigType.classification_error
  const isMultiType = newColumnConfig.type === AuditWorkflowColumnConfigType.multi_type

  const isDisabled =
    newColumnConfig.type.length == 0 ||
    newColumnConfig.alias.length == 0 ||
    (newColumnConfig.inputs.length == 0 && showInputs)

  return (
    <Modal maxWidth={'80%'} maxHeight={'80%'} isOpen={true} hideCloseButton={true}>
      {inputConfigModalState && (
        <QualityAuditWorkflowConfigColumnInputModal
          inputConfig={inputConfigModalState.input}
          onClose={() => setInputConfigModalState(undefined)}
          onSave={value => onInputSave(value, inputConfigModalState?.index)}
        />
      )}
      {columnConfigModalState && (
        <QualityAuditWorkflowConfigColumnFeatureModal
          columnConfig={newColumnConfig}
          onClose={() => setColumnConfigModalState(undefined)}
          onSave={config => {
            setColumnConfigModalState(undefined)
            setNewColumnConfig(config)
          }}
        />
      )}
      <ModalContainer>
        <ModalHeader>{i18n.title}</ModalHeader>
        <ModalBody>
          <PropertyContainer style={{ flexDirection: 'column' }}>
            <PropertySection style={{ flexDirection: 'row' }}>
              <Property style={{ flex: 1 }}>
                <PropertyTitle>{i18n.aliasLabel}</PropertyTitle>
                <Text size="small" css={{ flex: 1 }}>
                  {i18n.aliasDetails}
                </Text>
                <Input
                  style={{ width: '100%' }}
                  error={false}
                  onChange={event => onAliasChange(event.target.value)}
                  value={newColumnConfig?.alias}
                />
              </Property>
              <Property style={{ flex: 1 }}>
                <PropertyTitle>{i18n.renderLabel}</PropertyTitle>
                <Text size="small">{i18n.renderDetails}</Text>
                <Row
                  style={{
                    alignItems: 'center',
                    gap: `${spacing.X16}`,
                  }}
                >
                  <NewSelect
                    icon={SearchIcon}
                    placeholder={''}
                    onChange={value => onTypeChange(value ?? AuditWorkflowColumnConfigType.string)}
                    options={optionTypes}
                    value={newColumnConfig?.type ?? AuditWorkflowColumnConfigType.string}
                    isLoading={false}
                    isSearchable={true}
                  />
                  <Button
                    variant={'tertiary'}
                    style={{ margin: 0 }}
                    onClick={() => setColumnConfigModalState(columnConfig)}
                  >
                    <GearIcon size={18} color="systemSuccessRegular" />
                  </Button>
                </Row>
              </Property>
            </PropertySection>
            <PropertySection style={{ flexDirection: 'row' }}>
              <Property style={{ flex: 1 }}>
                <PropertyTitle>{i18n.inputsLabel}</PropertyTitle>
                <Row
                  style={{
                    alignItems: 'center',
                  }}
                >
                  <Text size="small">{i18n.inputsDetails}</Text>
                  <Spacer />
                  <Button
                    variant={'tertiary'}
                    style={{ margin: 0 }}
                    compact
                    disabled={!showInputs}
                    onClick={() =>
                      showInputModal(
                        isMultiType
                          ? {
                              subType: AuditWorkflowColumnConfigType.string,
                              subAlias: '',
                            }
                          : {},
                        inputs.length
                      )
                    }
                  >
                    <AddIcon size={18} color="systemSuccessRegular" />
                  </Button>
                </Row>
                <PropertyListContainer>
                  {inputs.map((input, index) => {
                    const key = `${input.name}-${input.type}-${input.group_name}}`
                    const label =
                      input.group_name && input.group_name.length > 0
                        ? `group/${input.group_name}`
                        : `${input.type}/${input.name}`
                    return (
                      <PropertyListElement key={key} style={{ flexDirection: 'column' }}>
                        <PropertyContainer css={cssPropertyContainer}>
                          <Input
                            style={{ width: '65px', background: 'white' }}
                            error={false}
                            type={'number'}
                            value={index + 1}
                            onChange={event => {
                              if (event.target.value.length == 0) return
                              const oldIndex = index
                              let newIndex = Number.parseInt(event.target.value) - 1
                              if (oldIndex > newIndex) {
                                newIndex += 2
                              } else {
                                newIndex -= 2
                              }
                              onColumnIndexChange(newIndex, oldIndex)
                            }}
                          />
                          {label}
                          <Spacer />
                          <Button
                            variant={'tertiary'}
                            css={cssPropertyListElementButton}
                            onClick={() => showInputModal(input, index)}
                          >
                            <GearIcon size={18} color="systemSuccessRegular" />
                          </Button>
                          <Button
                            variant={'tertiary'}
                            css={cssPropertyListElementButton}
                            onClick={() => onColumnIndexDelete(index)}
                          >
                            <CloseIcon size={18} color="systemSuccessRegular" />
                          </Button>
                        </PropertyContainer>
                        <Divider />
                        <PropertyContainer css={cssPropertyContainer}>
                          <Property style={{ padding: `${spacing.X8}` }}>
                            <Text size="small">{i18n.inputsFlaggable}</Text>
                            <SwitchBase
                              description={''}
                              disabled={isMultiType}
                              checked={input?.flaggable ?? false}
                              onClick={() =>
                                onInputChange(
                                  {
                                    ...input,
                                    flaggable: !(input?.flaggable ?? false),
                                  },
                                  index
                                )
                              }
                            />
                          </Property>
                          <Property style={{ padding: `${spacing.X8}` }}>
                            <Text size="small">{i18n.inputsSuggestible}</Text>
                            <SwitchBase
                              description={''}
                              disabled={isMultiType}
                              checked={input?.suggestible ?? false}
                              onClick={() =>
                                onInputChange(
                                  {
                                    ...input,
                                    suggestible: !(input?.suggestible ?? false),
                                  },
                                  index
                                )
                              }
                            />
                          </Property>
                          <Property style={{ padding: `${spacing.X8}` }}>
                            <Text size="small">{i18n.inputSearchable}</Text>
                            <SwitchBase
                              description={''}
                              disabled={isMultiType}
                              checked={input?.searchable ?? false}
                              onClick={() =>
                                onInputChange(
                                  {
                                    ...input,
                                    searchable: !(input?.searchable ?? false),
                                  },
                                  index
                                )
                              }
                            />
                          </Property>
                          {isMultiType && (
                            <>
                              <Property style={{ padding: `${spacing.X8}` }}>
                                <Text size="small">{i18n.subType}</Text>
                                <NewSelect
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: base => ({
                                      ...base,
                                      zIndex: 99999,
                                    }),
                                    menu: base => ({
                                      ...base,
                                      width: '200px',
                                    }),
                                  }}
                                  placeholder={''}
                                  onChange={value => {
                                    onInputChange(
                                      {
                                        ...input,
                                        subType: value as AuditWorkflowColumnConfigType,
                                      },
                                      index
                                    )
                                  }}
                                  options={subOptionTypes}
                                  value={input?.subType ?? AuditWorkflowColumnConfigType.string}
                                  isLoading={false}
                                  isSearchable={true}
                                />
                              </Property>
                              <Property style={{ padding: `${spacing.X8}` }}>
                                <Text size="small">{i18n.aliasLabel}</Text>
                                <Input
                                  style={{
                                    width: '100%',
                                    padding: `9px`,
                                    fontSize: fontSize.X15,
                                  }}
                                  error={false}
                                  onChange={event => {
                                    onInputChange(
                                      {
                                        ...input,
                                        subAlias: event.target.value,
                                      },
                                      index
                                    )
                                  }}
                                  value={input?.subAlias}
                                />
                              </Property>
                            </>
                          )}
                        </PropertyContainer>
                      </PropertyListElement>
                    )
                  })}
                </PropertyListContainer>
              </Property>
            </PropertySection>
          </PropertyContainer>
        </ModalBody>
        <ModalFooter>
          <Button variant={'secondary'} onClick={() => onClose()}>
            {i18n.close}
          </Button>
          <Button variant={'primary'} disabled={isDisabled} onClick={() => onSave(newColumnConfig)}>
            {i18n.save}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  )
}
